import * as Yup from 'yup';
import { TFunction } from 'i18next';
import { JPOnboardingSteps } from './use-job-positions-onboarding';
import { cn } from '@restworld/utils-common';
import {
  advertisedLocationField,
  domainInterestField,
  positiveSalaryField,
  restaurantIdField,
  staffRequestedField,
  workerMaxDistanceField,
} from './validation-fields';
import { isItalianPayTransparencyFlow } from '../../libs/salary/is-italian-venue';
import { isTrainingContractIncompatibleWithExperience } from './training-contract-experience';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ValidationSchema = Yup.ObjectSchema<any>;

/**
 * Step-specific validation schemas
 * Each step validates only the fields that are shown/required in that step
 *
 * @param t - Translation function from useTranslation hook
 * @returns Record of validation schemas for each step
 */
export const getStepValidationSchemas = (t: TFunction): Record<JPOnboardingSteps, ValidationSchema> => ({
  venue_selection: Yup.object().shape({
    restaurant_id: restaurantIdField(t),
  }) as ValidationSchema,

  venue_onboarding: Yup.object().shape({
    // No validation needed - venue onboarding has its own internal validation
  }) as ValidationSchema,

  role: Yup.object().shape({
    domain_interest: domainInterestField(t),
    occupation_id: Yup.string().required(t('steps.role.occupation_required')),
    experience: Yup.string().required(t('steps.role.experience_required')),
    experience_var: Yup.mixed().nullable(),
    experience_specifics: Yup.string()
      .nullable()
      .when('experience_var', {
        is: (val: { key?: string } | null) => val?.key && ['zero', 'experience_jp_zero'].includes(val.key),
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.required(t('steps.role.experience_specifics_required')),
      }),
    kitchen_station: Yup.string()
      .nullable()
      .max(200, t('errors.max_length', { count: 200 })),
  }) as ValidationSchema,

  skills: Yup.object().shape({
    languages: Yup.array()
      .of(
        Yup.object().shape({
          language: Yup.string().required(t('errors.required_field')),
          level: Yup.string().required(t('errors.required_field')),
        })
      )
      // The picker hides languages already chosen, so a duplicate cannot be created
      // here any more — but a draft saved before that change can still be restored with
      // one, and the backend refuses the whole job position when it sees a repeat
      // (`languages` is an embeds_many keyed on the language id). Catch it in the form
      // where it can be pointed at, rather than at submit where it cannot.
      //
      // The error is raised against the offending row's own `language` field, not the
      // array root. Nothing renders a root-level error: LanguagesSection reads per-index
      // `fieldState.error`, and the footer's scroll-to-error does
      // `getElementById(errorPath)`, which matches no element for a bare `languages`.
      // Left on the root, this rule would block Continue with no message at all — worse
      // than the bug it fixes.
      .test('unique-languages', t('errors.duplicate_language'), function (entries) {
        const seen = new Set<string>();

        for (const [index, entry] of (entries ?? []).entries()) {
          const language = entry?.language;
          if (!language) continue;

          if (seen.has(language)) {
            return this.createError({
              path: `${this.path}[${index}].language`,
              message: t('errors.duplicate_language'),
            });
          }

          seen.add(language);
        }

        return true;
      })
      .nullable(),
    // Add validation for skills fields
    // required_skills: Yup.array().min(1, 'Please select at least one skill'),
  }) as ValidationSchema,

  other_requirements: Yup.object().shape(
    {
      staff_requested: staffRequestedField(t),
      advertised_location: advertisedLocationField(t),
      worker_max_distance: workerMaxDistanceField(t).when('advertised_location', {
        is: 'local',
        then: (schema) => schema.required(t('errors.required_field')),
        otherwise: (schema) => schema.nullable(),
      }),
      ['seasonal?']: Yup.boolean().required(t('errors.required_field')),
      desired_start_type: Yup.string()
        .nullable()
        .when('seasonal?', {
          is: false,
          then: (schema) =>
            schema
              .required(t('errors.required_field'))
              .oneOf(['asap', 'exact_date', 'indicative_date'], t('errors.required_field')),
          otherwise: (schema) => schema.nullable(),
        }),
      desired_start_date: Yup.string()
        .nullable()
        .when('desired_start_type', {
          is: (val: string) => val === 'exact_date' || val === 'indicative_date',
          then: (schema) => schema.required(t('errors.required_field')),
          otherwise: (schema) => schema.nullable(),
        }),
      season_starts_at: Yup.string()
        .nullable()
        .when('seasonal?', {
          is: true,
          then: (schema) => schema.required(t('errors.required_field')),
          otherwise: (schema) => schema.nullable(),
        }),
      season_ends_at: Yup.string()
        .nullable()
        .when('seasonal?', {
          is: true,
          then: (schema) => schema.required(t('errors.required_field')),
          otherwise: (schema) => schema.nullable(),
        }),
      accommodation_choice: Yup.string()
        .nullable()
        .oneOf(['single', 'shared', 'none', null], t('errors.required_field'))
        .when(['seasonal?', 'season_starts_at', 'season_ends_at', 'desired_start_type', 'desired_start_date'], {
          is: (
            seasonal: boolean | null | undefined,
            seasonStartsAt: string | null | undefined,
            seasonEndsAt: string | null | undefined,
            desiredStartType: string | null | undefined,
            desiredStartDate: string | null | undefined
          ) => {
            const isChooseDate = desiredStartType === 'exact_date' || desiredStartType === 'indicative_date';
            return (
              (seasonal === true && !!seasonStartsAt && !!seasonEndsAt) ||
              (seasonal === false && (desiredStartType === 'asap' || (isChooseDate && !!desiredStartDate)))
            );
          },
          then: (schema) => schema.required(t('errors.required_field')),
          otherwise: (schema) => schema.nullable(),
        }),
      accommodation_details: Yup.string()
        .nullable()
        .max(500, t('errors.max_length', { count: 500 })),
    },
    [['desired_start_type', 'desired_start_date']]
  ) as ValidationSchema,

  contract_and_shifts: Yup.object().shape({
    contract_type: Yup.string()
      .required(t('errors.required_field'))
      // A training contract (apprendistato / tirocinio-stage) is meant to form
      // someone in the role, so it cannot coexist with an experience requirement.
      // Safety net mirroring the backend AdValidator gate: the option is already
      // hidden in ContractTypeSection, but edit mode can load an incoherent JP.
      .test(
        'training-contract-vs-experience',
        t('steps.contract_and_shifts.training_contract_experience_error'),
        function () {
          const { contract_type_var, experience_var } = this.parent as {
            contract_type_var?: { key?: string };
            experience_var?: { key?: string };
          };
          return !isTrainingContractIncompatibleWithExperience(contract_type_var?.key, experience_var?.key);
        }
      ),
    contract_type_var: Yup.mixed().nullable(),
    job_type: Yup.string().required(t('errors.required_field')),
    job_type_var: Yup.mixed().nullable(),
    days_required_per_week_number: Yup.number().required(t('errors.required_field')),
    shift_category: Yup.string().required(t('errors.required_field')),
    // Required only when job_type is part_time AND contract_type is not on_call.
    // Full-time implies 40h. On-call employers can't commit to a weekly figure.
    // Safe fallback: if either _var key is missing (e.g. edit mode loading a JP
    // whose backend payload didn't hydrate the variable objects, or a step
    // visited before contract_type/job_type are picked), treat the field as
    // optional rather than risk a false-positive required error.
    part_time_weekly_hours: Yup.string()
      .nullable()
      .max(200, t('errors.max_length', { count: 200 }))
      .when(['job_type_var', 'contract_type_var'], {
        is: (jobTypeVar: { key?: string } | null, contractTypeVar: { key?: string } | null) =>
          !!jobTypeVar?.key &&
          !!contractTypeVar?.key &&
          jobTypeVar.key === 'part_time' &&
          contractTypeVar.key !== 'on_call',
        then: (schema) =>
          schema
            .required(t('errors.required_field'))
            // A part-time contract is by definition under 40h/week (the job_type
            // option is literally labelled "Part time (meno di 40 ore settimanali)").
            // The field is free text ("20-30 ore", "circa 25"), so we parse every
            // integer and reject when the largest is >= 40, which contradicts
            // part-time and should be posted as Full time instead. Guards the
            // "part time da 40 ore settimanali" data inconsistency at the source.
            .test('part-time-hours-below-40', t('errors.part_time_hours_too_high'), (value) => {
              if (!value) return true; // emptiness is handled by .required above
              const numbers = value.match(/\d+/g);
              if (!numbers) return true; // no figure given (e.g. "mattine") -> let it pass
              return Math.max(...numbers.map((n) => parseInt(n, 10))) < 40;
            }),
        otherwise: (schema) => schema.nullable(),
      }),
  }) as ValidationSchema,

  salary: Yup.object()
    .shape({
      payment_type: Yup.string().required(t('errors.required_field')),
      payment_type_var: Yup.mixed().nullable(),
      min_salary: positiveSalaryField(t),
      max_salary: positiveSalaryField(t),
      salary: positiveSalaryField(t),
      // salary_type: Yup.string().required(t('errors.required_field')),
      // salary_month_number: Yup.number().required(t('errors.required_field')),
    })
    .test('salary-validation', '', function (value) {
      const { salary, min_salary, max_salary } = value || {};

      // If exact salary is provided, min and max are not required
      if (salary !== null && salary !== undefined) {
        return true;
      }

      const hasMin = min_salary !== null && min_salary !== undefined;
      const hasMax = max_salary !== null && max_salary !== undefined;

      // Min only or both min+max - OK
      if (hasMin) {
        return true;
      }

      // Only max without min - blocked ("fino a..." is not allowed)
      if (hasMax && !hasMin) {
        return this.createError({
          path: 'min_salary',
          message: t('steps.salary.salary_min_required'),
        });
      }

      // Neither salary nor min/max are provided - validation fails
      const error = this.createError({
        message: t('errors.required_field'),
      });
      error.inner = [
        this.createError({
          path: 'salary',
          message: t('errors.required_field'),
        }),
        this.createError({
          path: 'min_salary',
          message: t('errors.required_field'),
        }),
        this.createError({
          path: 'max_salary',
          message: t('errors.required_field'),
        }),
      ];
      return error;
    })
    .test('salary-sanity-check', '', function (value) {
      const { salary, min_salary, max_salary, payment_type_var } = value || {};
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const paymentTypeLabel = (payment_type_var as any)?.label?.toLowerCase() || '';
      const isHourly = paymentTypeLabel.includes('ore') || paymentTypeLabel.includes('orari');
      const isAnnual = paymentTypeLabel.includes('annual') || paymentTypeLabel.includes('ral');

      // Plausible bounds per pay type. The UPPER bound is the important guard:
      // it catches the most common data-entry mistake, an annual figure typed
      // while "Mensile" is selected (e.g. 30400 as a monthly salary), which the
      // lower-bound check alone lets through. Bounds are deliberately generous
      // so only clearly-wrong values are blocked.
      //   monthly: 100 .. 15000   (a >15k/month HoReCa wage is almost always a RAL)
      //   annual: 5000 .. 150000 (was 500000: that let a RAL typed with one
      //           zero too many through, a 30.000-350.000 range went live on
      //           2026-09-08; no real HoReCa RAL we publish comes near 150k)
      //   hourly:    4 .. (no cap)
      // No upper cap for hourly: min/max on hourly-paid positions aren't
      // reliably stored in €/hour (legacy rows hold monthly-magnitude figures),
      // so an hourly cap would false-positive on real offers.
      const minThreshold = isHourly ? 4 : isAnnual ? 5000 : 100;
      const maxThreshold = isHourly ? null : isAnnual ? 150000 : 15000;
      const tooLowMsg = isHourly
        ? t('steps.salary.salary_too_low_hourly')
        : isAnnual
        ? t('steps.salary.salary_too_low_annual')
        : t('steps.salary.salary_too_low_monthly');
      const tooHighMsg = isAnnual
        ? t('steps.salary.salary_too_high_annual')
        : t('steps.salary.salary_too_high_monthly');

      const valuesToCheck = [salary, min_salary, max_salary].filter(
        (v): v is number => v !== null && v !== undefined && v > 0
      );

      if (valuesToCheck.length === 0) return true;

      const errors: Yup.ValidationError[] = [];
      const checkField = (path: 'salary' | 'min_salary' | 'max_salary', v: number | null | undefined) => {
        if (v === null || v === undefined || v <= 0) return;
        if (v < minThreshold) errors.push(this.createError({ path, message: tooLowMsg }));
        else if (maxThreshold !== null && v > maxThreshold)
          errors.push(this.createError({ path, message: tooHighMsg }));
      };
      checkField('salary', salary);
      checkField('min_salary', min_salary);
      checkField('max_salary', max_salary);

      if (errors.length > 0) {
        const error = this.createError({ message: errors[0].message });
        error.inner = errors;
        return error;
      }

      return true;
    })
    .test('range-order', '', function (value) {
      // A salary range must be ordered: the maximum can't be lower than the minimum.
      const { salary, min_salary, max_salary } = value || {};
      if (salary !== null && salary !== undefined) return true; // exact mode, no range
      const hasMin = min_salary !== null && min_salary !== undefined;
      const hasMax = max_salary !== null && max_salary !== undefined;
      if (hasMin && hasMax && (max_salary as number) < (min_salary as number)) {
        return this.createError({
          path: 'max_salary',
          message: t('steps.salary.salary_max_below_min'),
        });
      }
      return true;
    })
    .test('italian-range-requires-both-bounds', '', function (value) {
      // Italian pay-transparency flow only. Art. 5 D.Lgs. 96/2026 asks for the initial pay or
      // its "fascia" (a bounded band), so a range must carry BOTH ends; an open-ended "da X"
      // (min only) isn't a fascia. Requiring both also avoids the estimate/confirmation deadlock
      // (estimate -> card -> confirm). The legacy/foreign flow still allows min-only "da X".
      const data = value as {
        salary?: number | null;
        min_salary?: number | null;
        max_salary?: number | null;
        restaurant?: { country?: string };
      };
      if (!isItalianPayTransparencyFlow(data?.restaurant?.country)) return true;
      const { salary, min_salary, max_salary } = data || {};
      if (salary !== null && salary !== undefined) return true; // exact figure, no range
      const hasMin = min_salary !== null && min_salary !== undefined;
      const hasMax = max_salary !== null && max_salary !== undefined;
      if (hasMin && !hasMax) {
        return this.createError({
          path: 'max_salary',
          message: t('steps.salary.salary_max_required'),
        });
      }
      return true;
    })
    .test('estimates-confirmed', '', function (value) {
      // Italian pay-transparency flow only: the employer must confirm the estimated
      // gross/net values before leaving the step. Foreign/legacy flows are unaffected.
      const data = value as { restaurant?: { country?: string }; salary_estimates_confirmed?: boolean } | undefined;
      if (!isItalianPayTransparencyFlow(data?.restaurant?.country)) return true;
      if (data?.salary_estimates_confirmed === true) return true;
      return this.createError({
        path: 'salary_estimates_confirmed',
        message: t('steps.salary.estimates_confirm_required'),
      });
    }) as ValidationSchema,

  benefits: Yup.object().shape({
    // Add validation for benefits fields (usually optional)
    // Example:
    // benefits: Yup.array(),
  }) as ValidationSchema,

  additional_notes: Yup.object().shape({
    // No validation needed - notes are optional
  }) as ValidationSchema,

  ai_autogeneration: Yup.object().shape({
    job_title: Yup.string().required(t('errors.required_field')),
    job_description: Yup.string().required(t('errors.required_field')),
    job_contract_description: Yup.string().required(t('errors.required_field')),
  }) as ValidationSchema,

  review: Yup.object().shape({
    // Final review step - validate all required fields are present
    // This ensures nothing was missed
    restaurant_id: restaurantIdField(t),
    domain_interest: domainInterestField(t),
    // Add other critical fields that must be present
  }) as ValidationSchema,

  company_and_payment: Yup.object().shape({
    // No validation needed - handled via sub-views within the step
  }) as ValidationSchema,

  success: Yup.object().shape({
    // No validation needed - success is a read-only confirmation screen
  }) as ValidationSchema,
});

/**
 * Helper function to get validation schema for a specific step
 * @param step - The current onboarding step
 * @param t - Translation function from useTranslation hook
 */
export const getValidationSchemaForStep = (step: JPOnboardingSteps, t: TFunction): ValidationSchema => {
  const schemas = getStepValidationSchemas(t);
  return schemas[step];
};

/**
 * Complete validation schema for the entire form
 * Used only when submitting the final form
 * @param t - Translation function from useTranslation hook
 */
export const getCompleteJobPositionSchema = (t: TFunction) =>
  Yup.object().shape({
    restaurant_id: restaurantIdField(t),
    domain_interest: domainInterestField(t),
    // Add all other required fields from all steps
    // occupation_id: Yup.string().required(t('errors.occupation_required')),
    // contract_type: Yup.string().required(t('errors.contract_type_required')),
    // salary_min: Yup.number().required(t('errors.salary_min_required')).positive(t('errors.salary_positive')),
    // ... etc
  });

export const ErrorMessage = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('mt-1 text-sm text-red-600 font-normal', className)} {...props} />
);
